'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PermutationAndCombinationCalculator() {
  const t = useTranslations("tools.permutationAndCombinationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [inputs, setInputs] = useState({
    n: 6,
    r: 2
  });
  const [results, setResults] = useState<{
    permutation: number;
    combination: number;
    permutationFormula: string;
    combinationFormula: string;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const calculatePermutation = (n: number, r: number): number => {
    if (r > n) return 0;
    if (r === 0) return 1;

    let result = 1;
    for (let i = 0; i < r; i++) {
      result *= (n - i);
    }
    return result;
  };

  const calculateCombination = (n: number, r: number): number => {
    if (r > n) return 0;
    if (r === 0 || r === n) return 1;

    r = Math.min(r, n - r);

    let result = 1;
    for (let i = 0; i < r; i++) {
      result *= (n - i);
      result /= (i + 1);
    }
    return Math.round(result);
  };

  const formatNumber = (num: number): string => {
    if (num === Infinity) return '∞';
    if (num >= 1e15) {
      return num.toExponential(2);
    }
    return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { n, r } = inputs;

    if (isNaN(n) || isNaN(r)) {
      setError(t('errorInvalidNumbers'));
      setResults(null);
      return;
    }

    if (n < 0 || r < 0) {
      setError(t('errorNegativeNumbers'));
      setResults(null);
      return;
    }

    if (r > n) {
      setError(t('errorRGreaterThanN'));
      setResults(null);
      return;
    }

    if (n > 170) {
      setError(t('errorNTooLarge'));
      setResults(null);
      return;
    }

    const permutation = calculatePermutation(n, r);
    const combination = calculateCombination(n, r);

    setResults({
      permutation,
      combination,
      permutationFormula: `${n}P${r} = ${n}! / (${n} - ${r})! = ${n}! / ${n - r}!`,
      combinationFormula: `${n}C${r} = ${n}! / (${r}! × (${n} - ${r})!) = ${n}! / (${r}! × ${n - r}!)`
    });
  };

  const handleClear = () => {
    setInputs({ n: 6, r: 2 });
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#059669',
    error: '#ef4444',
    warning: '#f59e0b',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginTop: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.12)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '900px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const toolSubtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginTop: '0.5rem' },
    { fontSize: '0.875rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const formStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    {},
    {}
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', color: colors.textPrimary, fontWeight: 600, marginBottom: '0.5rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
    { padding: '1rem 1.5rem', fontSize: '1.125rem' }
  );

  const inputHintStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginTop: '0.25rem', display: 'block' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.85rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      minHeight: '44px',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
      minHeight: '44px',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid #2563eb',
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultTitleStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const formulaDisplayStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      margin: '1rem 0',
      fontFamily: "'Courier New', monospace",
      textAlign: 'center' as const,
      color: colors.textPrimary,
    },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '1rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    {
      fontWeight: 800,
      color: colors.success,
      textAlign: 'center' as const,
      marginTop: '1rem',
      padding: '1rem',
      background: 'white',
      borderRadius: '0.5rem',
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      borderLeft: '4px solid #ef4444',
      color: '#991b1b',
      borderRadius: '0.5rem',
      marginTop: '1rem',
    },
    { padding: '0.875rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: '#fffbeb',
      borderLeft: '4px solid #f59e0b',
      borderRadius: '0.5rem',
      marginTop: '1.5rem',
    },
    { padding: '0.875rem', fontSize: '0.85rem' },
    { padding: '1rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.75rem',
      marginBottom: '1rem',
      border: '1px solid rgba(255, 200, 180, 0.15)',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={toolSubtitleStyle}>{t('toolSubtitle')}</p>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="n-value" style={labelStyle}>{t('nLabel')}</label>
              <input
                type="number"
                id="n-value"
                min="0"
                max="170"
                step="1"
                value={inputs.n}
                onChange={(e) => setInputs({ ...inputs, n: parseInt(e.target.value) || 0 })}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                required
              />
              <span style={inputHintStyle}>{t('nHint')}</span>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="r-value" style={labelStyle}>{t('rLabel')}</label>
              <input
                type="number"
                id="r-value"
                min="0"
                max="170"
                step="1"
                value={inputs.r}
                onChange={(e) => setInputs({ ...inputs, r: parseInt(e.target.value) || 0 })}
                style={inputStyle}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                required
              />
              <span style={inputHintStyle}>{t('rHint')}</span>
            </div>

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateButtonStyle}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                style={clearButtonStyle}
                onClick={handleClear}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t('clearButton')}
              </button>
            </div>
          </form>

          {error && (
            <div style={errorMessageStyle}>
              {error}
            </div>
          )}

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ ...h2Style, textAlign: 'center' as const }}>{t('resultsTitle')}</h3>

              <div style={resultCardStyle}>
                <div style={resultTitleStyle}>
                  <span>🔢</span>
                  <span>{t('permutationTitle')}</span>
                </div>
                <div style={formulaDisplayStyle}>
                  {results.permutationFormula}
                </div>
                <div style={resultValueStyle}>
                  {formatNumber(results.permutation)}
                </div>
              </div>

              <div style={{ ...resultCardStyle, borderLeftColor: '#7c3aed', background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' }}>
                <div style={resultTitleStyle}>
                  <span>🎯</span>
                  <span>{t('combinationTitle')}</span>
                </div>
                <div style={formulaDisplayStyle}>
                  {results.combinationFormula}
                </div>
                <div style={resultValueStyle}>
                  {formatNumber(results.combination)}
                </div>
              </div>

              <div style={infoBoxStyle}>
                <h4 style={{ color: '#92400e', fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  {t('differenceTitle')}
                </h4>
                <p style={{ color: '#78350f', fontSize: '0.9rem', lineHeight: 1.5, margin: 0 }}>
                  <strong>{t('permutationExplain')}</strong> {t('permutationExample')}
                </p>
                <p style={{ color: '#78350f', fontSize: '0.9rem', lineHeight: 1.5, marginTop: '0.5rem', marginBottom: 0 }}>
                  <strong>{t('combinationExplain')}</strong> {t('combinationExample')}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('step1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('step4')}</li>
          <li>{t('step5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insight1')}</p>
        <p style={paragraphStyle}>{t('insight2')}</p>
        <p style={paragraphStyle}>{t('insight3')}</p>
        <p style={paragraphStyle}>{t('insight4')}</p>
        <p style={paragraphStyle}>{t('insight5')}</p>
        <p style={paragraphStyle}>{t('insight6')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('formulasTitle')}</h3>
        <p style={paragraphStyle}>{t('formulasIntro')}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}><strong>{t('permutationFormula')}</strong> {t('permutationFormulaDesc')}</li>
          <li><strong>{t('combinationFormula')}</strong> {t('combinationFormulaDesc')}</li>
        </ul>

        <h3 style={h3Style}>{t('applicationsTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('application1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('application4')}</li>
          <li>{t('application5')}</li>
        </ul>

        <h3 style={h3Style}>{t('bestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('practice3')}</li>
          <li>{t('practice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq6Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.symbolab.com/calculator/math/permutation-combination" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation and Combination Calculator - Symbolab
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://permutation-combination-calculator.en.softonic.com/android" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation - Combination Calculator APK for Android
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://permutation-combination-calc.en.softonic.com/android" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation Combination Calc for Android
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.graphcalc.com/permutations-calculator/" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutations Calculator - GraphCalc
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://apps.apple.com/us/app/permutation-combination/id6448662601" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation Combination on the App Store
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculator.net/permutation-and-combination-calculator.html" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation and Combination Calculator
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://play.google.com/store/apps/details?id=com.iw.permutationandcombination&hl=en_US" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation & Combination Tool - Apps on Google Play
            </a>
          </li>
          <li>
            <a href="https://permutation-and-combination.en.softonic.com/iphone" style={refLinkStyle} target="_blank" rel="noopener noreferrer">
              Permutation and Combination for iPhone
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}